import { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '../hooks/useAuth'
import { Camera, Mic, Eye, Activity, Shield } from 'lucide-react'

export default function ProctoringOverlay() {
  const { user } = useAuthStore()
  const [isActive, setIsActive] = useState(false)
  const [cameraActive, setCameraActive] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Listen for exam start/end
  useEffect(() => {
    const handleExamStart = () => {
      setIsActive(true)
      startCamera()
    }
    const handleExamEnd = () => {
      setIsActive(false)
      stopCamera()
    }

    window.addEventListener('EXAM_START', handleExamStart)
    window.addEventListener('EXAM_END', handleExamEnd)

    return () => {
      window.removeEventListener('EXAM_START', handleExamStart)
      window.removeEventListener('EXAM_END', handleExamEnd)
      stopCamera()
    }
  }, [])

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
      setCameraActive(true)

      // Start periodic snapshots for AI proctoring
      const snapshotInterval = setInterval(() => {
        takeSnapshot()
      }, 30000) // Every 30 seconds

      return () => clearInterval(snapshotInterval)
    } catch {
      console.log('Camera access denied or not available')
    }
  }

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream
      stream.getTracks().forEach(track => track.stop())
      videoRef.current.srcObject = null
    }
    setCameraActive(false)
  }

  const takeSnapshot = () => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.drawImage(video, 0, 0)
      // In real app: send to server for AI face detection
      // canvas.toBlob(blob => uploadToServer(blob), 'image/jpeg')
    }
  }

  if (!isActive) return null

  return (
    <div className="fixed bottom-4 right-4 z-[9998]">
      {/* Camera Preview */}
      <div className="glass rounded-xl p-2 border border-primary/30">
        <div className="relative w-48 h-36 bg-slate-900 rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          <canvas ref={canvasRef} className="hidden" />

          {/* Status Overlay */}
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-danger/80 rounded text-xs text-white">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            REC
          </div>

          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
            <div className="flex items-center gap-1 text-xs text-slate-400">
              <Camera className="w-3 h-3" />
              <span>AI Proctoring</span>
            </div>
            <Shield className="w-3 h-3 text-success" />
          </div>
        </div>

        {/* Monitoring Stats */}
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-slate-500">
              <Eye className="w-3 h-3" /> Eye tracking
            </span>
            <span className="text-success">Active</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-slate-500">
              <Activity className="w-3 h-3" /> Activity monitor
            </span>
            <span className="text-success">Active</span>
          </div>
        </div>
      </div>
    </div>
  )
}
